import { z } from "zod";
import { LIMITS, emailSchema } from "./common.js";
import { conversationIdSchema, messageIdSchema } from "./ids.js";

/**
 * Where a message is in its life.
 * - received: stored, not routed yet
 * - setup: a provider's forward confirmation, shown in Setup
 * - ignored: screened out, or routed to Ignore
 * - held: parked for a member, with a reason
 * - waiting: a run waits for approval
 * - running: a run is queued or in progress
 * - done: the last run finished
 */
export const MESSAGE_STATES = ["received", "setup", "ignored", "held", "waiting", "running", "done"] as const;
export type MessageState = (typeof MESSAGE_STATES)[number];

/**
 * Whether a member still owes work on a message. An action with `followUp: true`
 * opens one when it finishes; a member closes it.
 * - none: no follow-up ever opened
 * - open: on the follow-up list
 * - closed: a member closed it
 */
export const FOLLOW_UP_STATES = ["none", "open", "closed"] as const;
export type FollowUpState = (typeof FOLLOW_UP_STATES)[number];

export const followUpSchema = z.discriminatedUnion("state", [
  z.strictObject({ state: z.literal("none") }),
  z.strictObject({ state: z.literal("open"), actionName: z.string(), openedAt: z.iso.datetime() }),
  z.strictObject({
    state: z.literal("closed"),
    actionName: z.string(),
    openedAt: z.iso.datetime(),
    closedAt: z.iso.datetime(),
    /** The trace name of the member or API key that closed it. */
    closedBy: z.string().min(1).max(200),
  }),
]);
export type FollowUp = z.infer<typeof followUpSchema>;
export type OpenFollowUp = Extract<FollowUp, { state: "open" }>;

export function openFollowUp(actionName: string, now: Date): OpenFollowUp {
  return { state: "open", actionName, openedAt: now.toISOString() };
}

export const HISTORY_OUTCOMES = ["done", "held", "waiting", "rejected", "failed", "ignored"] as const;
export type HistoryOutcome = (typeof HISTORY_OUTCOMES)[number];

const historyBase = {
  at: z.iso.datetime(),
  textPreview: z.string().max(LIMITS.textPreview),
};

/** One line of a conversation's history: what arrived and what mailhand did, or what it sent. */
export const historyEntrySchema = z.discriminatedUnion("direction", [
  z.strictObject({
    direction: z.literal("in"),
    ...historyBase,
    messageId: messageIdSchema,
    actionName: z.string().max(40).optional(),
    outcome: z.enum(HISTORY_OUTCOMES).optional(),
  }),
  z.strictObject({
    direction: z.literal("out"),
    ...historyBase,
    messageId: messageIdSchema,
  }),
]);
export type HistoryEntry = z.infer<typeof historyEntrySchema>;

export const attachmentMetaSchema = z.strictObject({
  filename: z.string(),
  contentType: z.string(),
  bytes: z.int().min(0),
});
export type AttachmentMeta = z.infer<typeof attachmentMetaSchema>;

/** One row of the activity list, as the control API returns it. */
export const messageRowSchema = z.strictObject({
  id: messageIdSchema,
  receivedAt: z.iso.datetime(),
  inboxAddress: emailSchema,
  from: z.string(),
  fromName: z.string().optional(),
  subject: z.string(),
  textPreview: z.string(),
  state: z.enum(MESSAGE_STATES),
  actionName: z.string().optional(),
  heldReason: z.string().optional(),
  followUp: z.enum(FOLLOW_UP_STATES),
  conversationId: conversationIdSchema.optional(),
});
export type MessageRow = z.infer<typeof messageRowSchema>;
